import { useUtilityStore } from '@/store/utility'

const GRID_RESIZE_HANDLE_CLASS = 'div-grid-resize-handle'
const GRID_MIN_COLUMN_WIDTH = 40
const GRID_SELECT_COLUMN_WIDTH = 40
const GRID_HEADER_EXTRA_PADDING = 24 // space for sort icon / padding
const GRID_RESIZE_STEP_MEDIUM_POINTER_DELTA = 4
const GRID_RESIZE_STEP_LARGE_POINTER_DELTA = 12
const GRID_RESIZE_STEP_MEDIUM = 8
const GRID_RESIZE_STEP_LARGE = 32
const GRID_RESIZE_TRANSITION = 'grid-template-columns 80ms ease-out'

interface ActiveGridResizeState {
  gridRoot: HTMLElement
  templateTargets: HTMLElement[]
  widths: number[]
  minWidths: number[]
  columnKeys: (string | null)[]
  tableId: string
  colIndex: number
  pointerId: number
  startX: number
  lastPointerX: number
  lastPointerDelta: number
  startWidth: number
  pendingWidth: number
  lastAppliedWidth: number
  onTemplateChange?: (templateColumns: string) => void
  rafId: number | null
  activeHandle: HTMLElement
}

let activeGridResizeState: ActiveGridResizeState | null = null
let gridResizeListenersBound = false

const normalizeWidth = (width: number, minWidth = GRID_MIN_COLUMN_WIDTH) => {
  return Math.max(minWidth, Math.round(width))
}

const getColumnStorageKey = (tableId: string, columnKey: string) => {
  return `${tableId}_grid_col_key_${encodeURIComponent(columnKey)}`
}

const getLegacyColumnStorageKey = (tableId: string, index: number) => {
  return `${tableId}_grid_col_${index}`
}

const isLegacyColumnStorageKey = (storageKey: string, tableId: string) => {
  const prefix = `${tableId}_grid_col_`
  if (!storageKey.startsWith(prefix)) return false

  return /^\d+$/.test(storageKey.slice(prefix.length))
}

const getCellWidth = (cell: HTMLElement) => {
  return normalizeWidth(cell.getBoundingClientRect().width || cell.offsetWidth || GRID_MIN_COLUMN_WIDTH)
}

const getConfiguredColumnWidth = (cell: HTMLElement) => {
  const raw = cell.dataset.colWidth
  if (!raw) return null

  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return null

  return normalizeWidth(parsed)
}

const setHandleBorder = (handle: HTMLElement, active: boolean) => {
  handle.style.borderRight = active ? '2px solid red' : ''
}

const getAdaptiveResizeStep = (pointerDelta: number) => {
  if (pointerDelta >= GRID_RESIZE_STEP_LARGE_POINTER_DELTA) return GRID_RESIZE_STEP_LARGE
  if (pointerDelta >= GRID_RESIZE_STEP_MEDIUM_POINTER_DELTA) return GRID_RESIZE_STEP_MEDIUM
  return 1
}

const snapByStep = (targetWidth: number, baseWidth: number, step: number) => {
  if (step <= 1) return normalizeWidth(targetWidth)
  return normalizeWidth(baseWidth + Math.round((targetWidth - baseWidth) / step) * step)
}

const buildGridTemplateColumns = (widths: number[]) => {
  return widths.map(width => `${normalizeWidth(width)}px`).join(' ')
}

const getGridTemplateTargets = (gridRoot: HTMLElement) => {
  const targets: HTMLElement[] = []
  const headerRow = gridRoot.querySelector('.div-grid-header') as HTMLElement | null
  if (headerRow) targets.push(headerRow)

  const rows = gridRoot.querySelectorAll('.div-grid-row, .div-grid-expanded-row')
  for (const row of rows) {
    targets.push(row as HTMLElement)
  }

  return targets
}

const applyTemplateColumns = (gridRoot: HTMLElement, templateColumns: string, templateTargets?: HTMLElement[]) => {
  const targets = templateTargets || getGridTemplateTargets(gridRoot)
  for (const target of targets) {
    target.style.gridTemplateColumns = templateColumns
  }
}

const setTemplateTransition = (templateTargets: HTMLElement[], transition: string) => {
  for (const target of templateTargets) {
    target.style.transition = transition
  }
}

const clearResizeVisuals = (state: ActiveGridResizeState | null) => {
  if (!state) return
  setTemplateTransition(state.templateTargets, '')
  setHandleBorder(state.activeHandle, false)
}

const syncWidthsFromRenderedHeader = (
  headerCells: HTMLElement[],
  widths: number[],
  minWidths: number[],
) => {
  for (let index = 0; index < headerCells.length; index++) {
    const cell = headerCells[index]
    const measuredWidth = getCellWidth(cell)
    const minWidth = minWidths[index] || GRID_MIN_COLUMN_WIDTH
    const normalizedMeasuredWidth = normalizeWidth(measuredWidth, minWidth)

    widths[index] = normalizedMeasuredWidth
  }
}

const persistAllWidths = (
  widths: number[],
  minWidths: number[],
  columnKeys: (string | null)[],
  tableId: string,
) => {
  const utilityStore = useUtilityStore()

  for (let index = 0; index < widths.length; index++) {
    const minWidth = minWidths[index] || GRID_MIN_COLUMN_WIDTH
    const normalizedWidth = normalizeWidth(widths[index] || minWidth, minWidth)
    widths[index] = normalizedWidth

    const columnKey = columnKeys[index]
    if (columnKey) {
      utilityStore.setColumnWidth(getColumnStorageKey(tableId, columnKey), normalizedWidth)
    }
  }
}

const cancelActiveGridResize = (gridRoot: HTMLElement) => {
  if (!activeGridResizeState || activeGridResizeState.gridRoot !== gridRoot) return

  if (activeGridResizeState.rafId !== null) {
    window.cancelAnimationFrame(activeGridResizeState.rafId)
  }

  if (activeGridResizeState.activeHandle.hasPointerCapture(activeGridResizeState.pointerId)) {
    activeGridResizeState.activeHandle.releasePointerCapture(activeGridResizeState.pointerId)
  }

  clearResizeVisuals(activeGridResizeState)
  activeGridResizeState = null
}

const flushGridResize = () => {
  if (!activeGridResizeState) return

  const minWidth = activeGridResizeState.minWidths[activeGridResizeState.colIndex] || GRID_MIN_COLUMN_WIDTH
  const rawNextWidth = normalizeWidth(activeGridResizeState.pendingWidth, minWidth)
  const pointerDelta = activeGridResizeState.lastPointerDelta
  const step = getAdaptiveResizeStep(pointerDelta)
  const snapBase = activeGridResizeState.lastAppliedWidth
  const snappedWidth = step === GRID_RESIZE_STEP_LARGE
    ? rawNextWidth
    : step === 1
      ? rawNextWidth
      : snapByStep(rawNextWidth, snapBase, step)

  if (snappedWidth === activeGridResizeState.lastAppliedWidth) return

  activeGridResizeState.lastAppliedWidth = snappedWidth
  activeGridResizeState.widths[activeGridResizeState.colIndex] = snappedWidth
  const templateColumns = buildGridTemplateColumns(activeGridResizeState.widths)

  // Keep drag updates DOM-only for smooth pointer tracking; reactive state commits when the pointer finishes.
  applyTemplateColumns(activeGridResizeState.gridRoot, templateColumns, activeGridResizeState.templateTargets)
}

const ensureGridResizeListeners = () => {
  if (gridResizeListenersBound) return

  document.addEventListener('pointermove', (e: PointerEvent) => {
    if (!activeGridResizeState || e.pointerId !== activeGridResizeState.pointerId) return

    e.preventDefault()

    activeGridResizeState.lastPointerDelta = Math.abs(e.clientX - activeGridResizeState.lastPointerX)
    activeGridResizeState.lastPointerX = e.clientX
    activeGridResizeState.pendingWidth = activeGridResizeState.startWidth + (e.clientX - activeGridResizeState.startX)

    if (activeGridResizeState.rafId !== null) return

    activeGridResizeState.rafId = window.requestAnimationFrame(() => {
      if (!activeGridResizeState) return

      flushGridResize()
      activeGridResizeState.rafId = null
    })
  })

  const finishGridResize = (e: PointerEvent) => {
    if (!activeGridResizeState || e.pointerId !== activeGridResizeState.pointerId) return

    const completedState = activeGridResizeState

    if (completedState.rafId !== null) {
      window.cancelAnimationFrame(completedState.rafId)
      completedState.rafId = null
    }

    const minWidth = completedState.minWidths[completedState.colIndex] || GRID_MIN_COLUMN_WIDTH
    const finalWidth = normalizeWidth(completedState.pendingWidth, minWidth)
    completedState.widths[completedState.colIndex] = finalWidth

    const templateColumns = buildGridTemplateColumns(completedState.widths)

    applyTemplateColumns(completedState.gridRoot, templateColumns, completedState.templateTargets)
    completedState.onTemplateChange?.(templateColumns)

    if (completedState.activeHandle.hasPointerCapture(completedState.pointerId)) {
      completedState.activeHandle.releasePointerCapture(completedState.pointerId)
    }

    clearResizeVisuals(completedState)

    persistAllWidths(
      completedState.widths,
      completedState.minWidths,
      completedState.columnKeys,
      completedState.tableId,
    )
    activeGridResizeState = null
  }

  document.addEventListener('pointerup', finishGridResize)
  document.addEventListener('pointercancel', finishGridResize)

  gridResizeListenersBound = true
}

export const applyDivGridResizableColumns = (
  gridRoot: HTMLElement,
  tableId: string,
  onTemplateChange?: (templateColumns: string) => void,
) => {
  const cleanup = () => {
    cancelActiveGridResize(gridRoot)
    gridRoot.querySelectorAll(`.${GRID_RESIZE_HANDLE_CLASS}`).forEach(handle => handle.remove())
  }

  cleanup()

  ensureGridResizeListeners()

  const headerRow = gridRoot.querySelector('.div-grid-header') as HTMLElement | null
  if (!headerRow) return cleanup

  const headerCells = Array.from(headerRow.querySelectorAll('.grid-cell')) as HTMLElement[]

  if (headerCells.length <= 1) return cleanup

  const hasLeadingSelectColumn = headerCells[0]?.classList.contains('select-col') ?? false
  const columnKeys = headerCells.map(cell => cell.dataset.colKey || null)
  const minWidths = headerCells.map(cell => getConfiguredColumnWidth(cell) || GRID_MIN_COLUMN_WIDTH)

  const utilityStore = useUtilityStore()

  // Migrate the previous index-based widths to stable column keys once. Any
  // unmatched legacy entries belonged to currently hidden columns and cannot
  // be mapped safely, so remove them instead of assigning them to the wrong
  // column on a later header update.
  for (let index = 0; index < headerCells.length; index++) {
    const columnKey = columnKeys[index]
    if (!columnKey) continue

    const keyedStorageKey = getColumnStorageKey(tableId, columnKey)
    const legacyStorageKey = getLegacyColumnStorageKey(tableId, index)
    const keyedWidth = utilityStore.getColumnWidth(keyedStorageKey)
    const legacyWidth = utilityStore.getColumnWidth(legacyStorageKey)

    if (!keyedWidth && legacyWidth) {
      utilityStore.setColumnWidth(keyedStorageKey, legacyWidth)
    }
  }

  Object.keys(utilityStore.columnWidths)
    .filter(storageKey => isLegacyColumnStorageKey(storageKey, tableId))
    .forEach(storageKey => utilityStore.clearColumnWidth(storageKey))

  const measureHeaderContentWidth = (cell: HTMLElement) => {
    // Prefer a contained content element (project uses .d-flex align-center),
    // otherwise fall back to first element or the cell itself.
    const contentEl = cell.querySelector('.d-flex') as HTMLElement | null || cell.firstElementChild as HTMLElement | null || cell
    const rect = contentEl.getBoundingClientRect()
    return Math.round(rect.width) + GRID_HEADER_EXTRA_PADDING
  }

  // Compute natural widths for columns without a saved keyed width.
  const minimalWidths = headerCells.map((cell, index) => {
    const contentMinWidth = Math.max(GRID_MIN_COLUMN_WIDTH, measureHeaderContentWidth(cell))
    const configuredMinWidth = minWidths[index] || GRID_MIN_COLUMN_WIDTH
    return Math.max(contentMinWidth, configuredMinWidth)
  })

  const totalMin = minimalWidths.reduce((sum, width) => sum + width, 0)
  // The grid uses max-content sizing, so its own width may still reflect the
  // previous set of columns during an in-place header update. Size unsaved
  // columns against the visible scrollport instead.
  const scrollportWidth = gridRoot.parentElement?.clientWidth || 0
  const available = Math.max(scrollportWidth, totalMin)
  const factor = totalMin < available ? available / totalMin : 1
  const naturalWidths = minimalWidths.map(width => normalizeWidth(Math.round(width * factor)))

  const widths = headerCells.map((_, index) => {
    const columnKey = columnKeys[index]
    const minWidth = minWidths[index] || GRID_MIN_COLUMN_WIDTH
    const savedWidth = columnKey
      ? utilityStore.getColumnWidth(getColumnStorageKey(tableId, columnKey))
      : null

    return savedWidth
      ? normalizeWidth(savedWidth, minWidth)
      : naturalWidths[index]
  })

  if (hasLeadingSelectColumn) {
    widths[0] = GRID_SELECT_COLUMN_WIDTH
    minWidths[0] = GRID_SELECT_COLUMN_WIDTH
  }

  const templateTargets = getGridTemplateTargets(gridRoot)
  const templateColumns = buildGridTemplateColumns(widths)
  applyTemplateColumns(gridRoot, templateColumns, templateTargets)
  onTemplateChange?.(templateColumns)

  const firstResizableIndex = hasLeadingSelectColumn ? 1 : 0

  for (let i = firstResizableIndex; i < headerCells.length; i++) {
    const cell = headerCells[i]

    if (Array.from(cell.children).some(child => child.classList.contains(GRID_RESIZE_HANDLE_CLASS))) {
      continue
    }

    const handle = document.createElement('div')
    handle.classList.add(GRID_RESIZE_HANDLE_CLASS)

    handle.style.position = 'absolute'
    handle.style.top = '0'
    handle.style.right = '0'
    handle.style.width = window.matchMedia('(any-pointer: coarse)').matches ? '12px' : '6px'
    handle.style.height = '100%'
    handle.style.cursor = 'col-resize'
    handle.style.userSelect = 'none'
    handle.style.touchAction = 'none'

    if (!cell.style.position || cell.style.position === 'static') {
      cell.style.position = 'relative'
    }

    handle.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.button !== 0 || activeGridResizeState) return

      e.preventDefault()
      e.stopPropagation()

      // Re-sync from live DOM widths so drag starts from current rendered layout.
      syncWidthsFromRenderedHeader(headerCells, widths, minWidths)

      const cellRect = cell.getBoundingClientRect()
      const liveTemplateTargets = getGridTemplateTargets(gridRoot)

      activeGridResizeState = {
        gridRoot,
        templateTargets: liveTemplateTargets,
        widths,
        minWidths,
        columnKeys,
        tableId,
        colIndex: i,
        pointerId: e.pointerId,
        startX: e.clientX,
        lastPointerX: e.clientX,
        lastPointerDelta: 0,
        startWidth: normalizeWidth(
          cellRect.width || cell.offsetWidth || GRID_MIN_COLUMN_WIDTH,
          minWidths[i] || GRID_MIN_COLUMN_WIDTH,
        ),
        pendingWidth: normalizeWidth(
          cellRect.width || cell.offsetWidth || GRID_MIN_COLUMN_WIDTH,
          minWidths[i] || GRID_MIN_COLUMN_WIDTH,
        ),
        lastAppliedWidth: normalizeWidth(
          cellRect.width || cell.offsetWidth || GRID_MIN_COLUMN_WIDTH,
          minWidths[i] || GRID_MIN_COLUMN_WIDTH,
        ),
        onTemplateChange,
        rafId: null,
        activeHandle: handle,
      }
      handle.setPointerCapture(e.pointerId)
      setTemplateTransition(liveTemplateTargets, GRID_RESIZE_TRANSITION)
      // visually indicate active drag
      setHandleBorder(handle, true)
    })

    handle.addEventListener('pointerenter', () => {
      setHandleBorder(handle, true)
    })

    handle.addEventListener('pointerleave', () => {
      // keep border while dragging this handle
      if (activeGridResizeState?.activeHandle === handle) return
      setHandleBorder(handle, false)
    })

    handle.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation()
    })

    cell.appendChild(handle)
  }

  return cleanup
}
